import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MessageCircleHeart, TrendingUp, Sparkles, Award, Repeat, Check, HeartHandshake, ArrowRight, Heart } from "lucide-react";

const BENEFITS = [
  { icon: MessageCircleHeart, title: "Room to keep talking", desc: "About 4× the daily conversations with your faith-based Companion, so support is there when you need it most." },
  { icon: TrendingUp, title: "Deeper progress trends", desc: "See your 30- and 90-day patterns — not just this week — and watch the long arc of your freedom." },
  { icon: Sparkles, title: "Craving & trigger insights", desc: "Understand when and why cravings hit, with patterns drawn from your own journal over time." },
  { icon: Award, title: "Longer-term milestones", desc: "Healing markers that stretch months and years ahead, so you always have something to walk toward." },
  { icon: Repeat, title: "More fresh ideas", desc: "Expanded trigger swaps and habit suggestions whenever you need a different way through." },
];

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  const [granted, setGranted] = useState(false);
  const isMember = !!user?.membership?.is_member;

  const goSponsored = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await client.post("/membership/sponsored");
      await checkAuth();
      setGranted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("We couldn't set that up just now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (granted || isMember) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="membership-member-state">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-brand-navy text-brand-ivory border border-brand-gold/20 p-8 shadow-soft text-center">
          <div className="rounded-full bg-brand-teal/20 w-16 h-16 flex items-center justify-center mx-auto mb-5">
            <Check className="w-8 h-8 text-brand-teal" />
          </div>
          <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-2">You're In</p>
          <h1 className="font-serif text-3xl sm:text-4xl mb-4">You're a Not By Will Member.</h1>
          <p className="font-scripture italic text-scripture-lg text-brand-ivory-muted max-w-lg mx-auto">
            Your journey matters. Your membership has been covered by the Not By Will community. When you're able,
            you can help someone else begin theirs.
          </p>
        </motion.div>

        <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="membership-included">
          <h2 className="font-serif text-2xl text-olive mb-5">What's now open to you</h2>
          <ul className="space-y-4">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.title} className="flex gap-4">
                  <div className="rounded-xl bg-sage p-2.5 h-fit shrink-0"><Icon className="w-5 h-5 text-brand-teal-dark" /></div>
                  <div>
                    <p className="font-medium text-olive">{b.title}</p>
                    <p className="text-sm text-olive/70">{b.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button data-testid="membership-go-progress" onClick={() => navigate("/app/tracker")}
            className="flex-1 rounded-full bg-terracotta hover:bg-terracotta-soft text-white gap-2 min-h-[48px]">
            See your deeper insights <ArrowRight className="w-4 h-4" />
          </Button>
          <Button data-testid="membership-go-mission" onClick={() => navigate("/app/mission")} variant="outline"
            className="flex-1 rounded-full border-olive/20 text-olive hover:bg-sage gap-2 min-h-[48px]">
            <Heart className="w-4 h-4" /> Help someone else begin
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="membership-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">Not By Will Member</p>
        <h1 className="font-serif text-display text-olive">Continue your journey</h1>
        <p className="text-olive/70 mt-3 max-w-2xl">
          You've been doing real work. Membership doesn't unlock your recovery — that's yours, and it's always free.
          It simply opens up more room to be supported and deeper insight into your own patterns, so you can keep going.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="membership-benefits">
        {BENEFITS.map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.title} className="rounded-3xl bg-card border border-border p-6 shadow-soft">
              <div className="rounded-xl bg-sage p-2.5 w-fit mb-3"><Icon className="w-5 h-5 text-brand-teal-dark" /></div>
              <p className="font-serif text-lg text-olive mb-1">{b.title}</p>
              <p className="text-sm text-olive/70">{b.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Sponsored access — the way in, no payment needed */}
      <div className="rounded-3xl bg-brand-navy text-brand-ivory border border-brand-gold/20 p-8 shadow-soft" data-testid="sponsored-card">
        <div className="rounded-xl bg-brand-teal/20 p-2.5 w-fit mb-4"><HeartHandshake className="w-6 h-6 text-brand-teal" /></div>
        <h2 className="font-serif text-2xl sm:text-3xl mb-2">Can't afford membership?</h2>
        <p className="text-brand-ivory-muted mb-6 max-w-lg">
          Freedom shouldn't depend on what you can afford. Your membership can be covered by the Not By Will community —
          no forms, no proof, no questions. Just say the word.
        </p>
        <Button data-testid="sponsored-continue-btn" onClick={goSponsored} disabled={busy}
          className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-brand-navy font-medium gap-2 px-7 py-6 text-base">
          {busy ? "One moment…" : "Continue with Sponsored Access"}
        </Button>
        <p className="text-xs text-brand-ivory-muted/70 mt-4">Paid membership options are coming soon.</p>
      </div>

      <p className="text-sm text-stone text-center">
        Prefer to give back?{" "}
        <button data-testid="membership-support-link" onClick={() => navigate("/app/mission")} className="text-brand-teal-dark hover:underline font-medium">
          Help someone else break the chain
        </button>
      </p>
    </div>
  );
}
